import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, CheckCircle2, Lock } from "lucide-react";

const GRAD = "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (!token) { toast.error("Lien invalide"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 3000);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: "#f4f6ff", fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: "rgba(139,92,246,.12)" }} />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[100px] pointer-events-none"
        style={{ background: "rgba(59,130,246,.10)" }} />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/login" className="text-sm inline-flex items-center gap-2 mb-6 font-medium hover:opacity-70 transition"
          style={{ color: "#4a4a6a" }}>
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>

        <div className="rounded-3xl p-8 sm:p-10 border"
          style={{ background: "#fff", borderColor: "#e2e8f8", boxShadow: "0 20px 60px -20px rgba(59,130,246,.2)" }}>
          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-extrabold"
              style={{ background: GRAD, boxShadow: "0 4px 12px -4px rgba(59,130,246,.6)" }}>N</div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.2rem", color: "#0d0d1a" }}>
              Neobk<span style={{ background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.</span>Pro
            </span>
          </div>

          {done ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: "rgba(16,185,129,.1)" }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: "#10b981" }} />
              </div>
              <h1 className="text-2xl font-bold mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#0d0d1a", letterSpacing: "-0.02em" }}>
                Mot de passe mis à jour !
              </h1>
              <p className="text-sm" style={{ color: "#4a4a6a" }}>Redirection vers la connexion…</p>
            </div>
          ) : !token ? (
            <div className="text-center py-4">
              <p className="font-semibold mb-4" style={{ color: "#ef4444" }}>Lien invalide ou expiré.</p>
              <Link to="/forgot-password" className="text-sm font-semibold hover:underline" style={{ color: "#3B82F6" }}>
                Demander un nouveau lien
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: "rgba(59,130,246,.08)" }}>
                  <Lock className="w-6 h-6" style={{ color: "#3B82F6" }} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold leading-tight"
                    style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#0d0d1a", letterSpacing: "-0.02em" }}>
                    Nouveau mot de passe
                  </h1>
                  <p className="text-sm mt-0.5" style={{ color: "#4a4a6a" }}>6 caractères minimum</p>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: "#4a4a6a" }}>
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      required
                      minLength={6}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pr-10 px-3 py-2.5 rounded-xl border-2 outline-none focus:border-blue-500 transition"
                      style={{ borderColor: "#e2e8f8", color: "#0d0d1a", background: "#fff" }}
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70 transition"
                      style={{ color: "#4a4a6a" }}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: "#4a4a6a" }}>
                    Confirmer le mot de passe
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirm}
                      required
                      minLength={6}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pr-10 px-3 py-2.5 rounded-xl border-2 outline-none focus:border-blue-500 transition"
                      style={{
                        borderColor: confirm && confirm !== password ? "#ef4444" : "#e2e8f8",
                        color: "#0d0d1a", background: "#fff"
                      }}
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70 transition"
                      style={{ color: "#4a4a6a" }}>
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirm && confirm !== password && (
                    <p className="text-xs mt-1 ml-1" style={{ color: "#ef4444" }}>Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={busy || !password || !confirm}
                  className="w-full py-3 rounded-full text-white font-bold text-base transition disabled:opacity-50 hover:-translate-y-0.5 mt-2"
                  style={{ background: GRAD, boxShadow: "0 8px 20px -8px rgba(59,130,246,.5)" }}
                >
                  {busy ? "Enregistrement…" : "Enregistrer le mot de passe"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
